import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { BsScissors, BsDownload, BsFileEarmarkPdf } from 'react-icons/bs';
import { FiUploadCloud } from 'react-icons/fi';
import { PDFDocument } from 'pdf-lib';
import SEO from '../components/SEO';
import { pdfSplitSchemas } from '../schemas';
import './ToolPage.css';

const PDFSplit = () => {
  const [file, setFile] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedPages, setSelectedPages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [splitMode, setSplitMode] = useState('extract'); // 'extract' or 'range'
  const [rangeStart, setRangeStart] = useState(1);
  const [rangeEnd, setRangeEnd] = useState(1);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      await loadPDF(selectedFile);
    }
  };

  const loadPDF = async (pdfFile) => {
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      const pageCount = pdf.getPageCount();
      
      setFile(pdfFile);
      setPdfDoc(pdf);
      setTotalPages(pageCount);
      setRangeEnd(pageCount);
      setSelectedPages([]);
    } catch (error) {
      console.error('Error loading PDF:', error);
      alert('Error loading PDF. Please try again.');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      await loadPDF(droppedFile);
    }
  };

  const togglePageSelection = (pageNum) => {
    setSelectedPages(prev => 
      prev.includes(pageNum) 
        ? prev.filter(p => p !== pageNum)
        : [...prev, pageNum].sort((a, b) => a - b)
    );
  };

  const selectAllPages = () => {
    setSelectedPages(Array.from({ length: totalPages }, (_, i) => i + 1));
  };

  const deselectAllPages = () => {
    setSelectedPages([]);
  };

  const extractPages = async () => {
    if (!pdfDoc) return;
    
    let pagesToExtract = [];
    
    if (splitMode === 'extract') {
      if (selectedPages.length === 0) {
        alert('Please select at least one page to extract');
        return;
      }
      pagesToExtract = selectedPages.map(p => p - 1); // Convert to 0-indexed
    } else {
      if (rangeStart > rangeEnd || rangeStart < 1 || rangeEnd > totalPages) {
        alert('Please enter a valid page range');
        return;
      }
      pagesToExtract = Array.from(
        { length: rangeEnd - rangeStart + 1 }, 
        (_, i) => rangeStart - 1 + i
      );
    }

    setIsProcessing(true);

    try {
      const newPdf = await PDFDocument.create();
      const copiedPages = await newPdf.copyPages(pdfDoc, pagesToExtract);
      copiedPages.forEach(page => newPdf.addPage(page));

      const pdfBytes = await newPdf.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `extracted-pages-${Date.now()}.pdf`;
      link.click();
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error extracting pages:', error);
      alert('Error extracting pages. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="tool-page">
      <SEO 
        title="Split PDF Online – Extract Pages from PDF Free"
        description="Split a PDF into multiple files or extract specific pages online free. Fast, secure PDF splitter tool — no software needed."
        keywords="split pdf, extract pdf pages, pdf splitter online"
        path="/pdf-split"
        schema={pdfSplitSchemas}
      />
      <section className="tool-hero">
        <div className="tool-hero-container">
          <motion.div 
            className="tool-icon-large"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <BsScissors />
          </motion.div>

          <motion.h1
            className="tool-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Split PDF & Extract Pages
          </motion.h1>

          <motion.p
            className="tool-description"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Extract specific pages or ranges from your PDF document
          </motion.p>
        </div>
      </section>

      <section className="tool-workspace">
        <div className="workspace-container">
          {!file ? (
            <motion.div
              className={`upload-zone ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => fileInputRef.current?.click()}
            >
              <FiUploadCloud className="upload-icon" />
              <h3>Drop PDF file here or click to browse</h3>
              <p>Upload a PDF to split or extract pages</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </motion.div>
          ) : (
            <div className="pdf-editor-layout">
              <div className="pdf-control-panel">
                <div className="file-info-header">
                  <BsFileEarmarkPdf className="file-icon-large" />
                  <div>
                    <h3>{file.name}</h3>
                    <p>{totalPages} pages</p>
                  </div>
                </div>

                <div className="split-mode-tabs">
                  <button
                    className={`mode-tab ${splitMode === 'extract' ? 'active' : ''}`}
                    onClick={() => setSplitMode('extract')}
                  >
                    Select Pages
                  </button>
                  <button
                    className={`mode-tab ${splitMode === 'range' ? 'active' : ''}`}
                    onClick={() => setSplitMode('range')}
                  >
                    Page Range
                  </button>
                </div>

                {splitMode === 'extract' ? (
                  <div className="page-selection-controls">
                    <div className="selection-actions">
                      <button className="btn-secondary-small" onClick={selectAllPages}>
                        Select All
                      </button>
                      <button className="btn-secondary-small" onClick={deselectAllPages}>
                        Clear All
                      </button>
                    </div>

                    <div className="pages-grid">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                        <motion.div
                          key={pageNum}
                          className={`page-thumbnail ${selectedPages.includes(pageNum) ? 'selected' : ''}`}
                          onClick={() => togglePageSelection(pageNum)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <div className="page-number">{pageNum}</div>
                          {selectedPages.includes(pageNum) && (
                            <div className="selection-badge">
                              ✓
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </div>

                    <div className="selection-summary">
                      Selected: {selectedPages.length} pages
                    </div>
                  </div>
                ) : (
                  <div className="range-controls">
                    <div className="range-inputs">
                      <div className="input-group">
                        <label>From Page</label>
                        <input
                          type="number"
                          min="1"
                          max={totalPages}
                          value={rangeStart}
                          onChange={(e) => setRangeStart(parseInt(e.target.value) || 1)}
                          className="range-input"
                        />
                      </div>
                      <div className="input-group">
                        <label>To Page</label>
                        <input
                          type="number"
                          min="1"
                          max={totalPages}
                          value={rangeEnd}
                          onChange={(e) => setRangeEnd(parseInt(e.target.value) || 1)}
                          className="range-input"
                        />
                      </div>
                    </div>
                    <div className="range-preview">
                      Will extract pages {rangeStart} to {rangeEnd} ({rangeEnd - rangeStart + 1} pages)
                    </div>
                  </div>
                )}
              </div>

              <div className="action-buttons">
                <motion.button
                  className="btn-primary-large"
                  onClick={extractPages}
                  disabled={isProcessing}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <BsDownload />
                  {isProcessing ? 'Extracting...' : 'Extract Pages'}
                </motion.button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PDFSplit;
